// Library Methods
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

// Components
import Box from "@mui/material/Box";

// Constants
const INK = "#202338";
const INK2 = "#5B6D7A";
const WARM = "#E2772E";
const LINE = "#5B6D7A80";

// The canvas the figure is laid out in. It scales to whatever width the card gives it, so
// these are proportions rather than pixels.
const WIDTH = 600;
const HEIGHT = 128;
const MARGIN = 54;
const AXIS_Y = 90;

// Where a label can sit, furthest from the axis first. A reading drops to the first row it
// fits on, so two directions a fraction of a percent apart never print on top of each other.
const LABEL_ROWS = [30, 48, 66];

// Roughly how wide a character of the label font is, which is all the arithmetic below needs:
// it is deciding whether two labels overlap, not typesetting them.
const CHARACTER_WIDTH = 6.4;
const LABEL_PADDING = 10;

// The axis never shows less than this either side of zero, so a section where every direction
// came out small does not get a scale magnified to the point that it looks alarming.
const MINIMUM_DOMAIN = 10;
const TICK_STEP = 2;

export type SizeScalePointType = {
  name: string;
  // Signed, with the payload's convention: negative is the LEFT eye seeing the larger image.
  percent: number;
};

type SizeScaleType = {
  points: Array<SizeScalePointType>;
  significantAt: number;
};

type LaidOutLabelType = {
  text: string;
  percent: number;
  x: number;
  textX: number;
  anchor: "start" | "middle" | "end";
  y: number;
};

/**
 * Every direction on one axis, so "does this matter" is a position in a picture rather than a
 * word in a table.
 *
 * The band beyond the significance threshold is shaded at both ends and the readings inside it
 * are drawn warm. That is the only place on the report that says a difference is large, which
 * is why nothing underneath repeats it in words.
 */
const SizeScale = ({ points, significantAt }: SizeScaleType) => {
  const { t } = useTranslation();

  const track = WIDTH - 2 * MARGIN;
  const centreX = MARGIN + track / 2;
  const half = track / 2;

  // Rounded outwards past the largest reading so a dot never sits on the end of the axis, and
  // floored at MINIMUM_DOMAIN so the scale does not change meaning between reports.
  const domain = useMemo(
    () =>
      Math.max(
        MINIMUM_DOMAIN,
        Math.ceil(Math.max(...points.map((point) => Math.abs(point.percent)))) +
          2
      ),
    [points]
  );

  const scaleX = useMemo(
    () => (percent: number) => centreX + (percent / domain) * half,
    [centreX, domain, half]
  );

  const ticks = useMemo(() => {
    const values: Array<number> = [0];
    for (let value = TICK_STEP; value <= domain; value += TICK_STEP) {
      values.push(value, -value);
    }
    return values;
  }, [domain]);

  // Laid out left to right, each label dropping to the first row it fits on and the outermost
  // two pulled inside the canvas so neither is clipped by the viewBox.
  const labels = useMemo(() => {
    const occupied: Array<Array<[number, number]>> = LABEL_ROWS.map(() => []);
    const laid: Array<LaidOutLabelType> = [];

    [...points]
      .sort((a, b) => a.percent - b.percent)
      .forEach((point) => {
        const text = `${point.name} ${Math.abs(point.percent).toFixed(1)}%`;
        const width = text.length * CHARACTER_WIDTH + LABEL_PADDING;
        const x = scaleX(point.percent);

        let left = x - width / 2;
        let right = x + width / 2;
        let anchor: LaidOutLabelType["anchor"] = "middle";
        let textX = x;

        if (left < 2) {
          [left, right, anchor, textX] = [2, 2 + width, "start", 2];
        } else if (right > WIDTH - 2) {
          [left, right, anchor, textX] = [
            WIDTH - 2 - width,
            WIDTH - 2,
            "end",
            WIDTH - 2,
          ];
        }

        let row = 0;
        while (
          row < LABEL_ROWS.length - 1 &&
          occupied[row].some(([a, b]) => !(right < a || left > b))
        ) {
          row += 1;
        }

        occupied[row].push([left, right]);
        laid.push({
          text,
          percent: point.percent,
          x,
          textX,
          anchor,
          y: LABEL_ROWS[row],
        });
      });

    return laid;
  }, [points, scaleX]);

  return (
    <Box
      component="svg"
      role="img"
      data-testid="aniseikonia-size-scale"
      aria-label={t("Image Size Difference for Each Direction")}
      viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
      sx={{ display: "block", width: "100%", mx: "auto" }}
    >
      {/* The two shaded ends: at or beyond the threshold, in both directions. */}
      {[-1, 1].map((sign) => {
        const from = scaleX(sign * significantAt);
        const to = scaleX(sign * domain);
        return (
          <rect
            key={`band-${sign}`}
            x={Math.min(from, to)}
            y={AXIS_Y - 9}
            width={Math.abs(to - from)}
            height={18}
            fill={`${WARM}18`}
          />
        );
      })}

      <line
        x1={MARGIN}
        y1={AXIS_Y}
        x2={WIDTH - MARGIN}
        y2={AXIS_Y}
        stroke={LINE}
      />

      {ticks.map((value) => (
        <g key={`tick-${value}`}>
          <line
            x1={scaleX(value)}
            y1={AXIS_Y}
            x2={scaleX(value)}
            y2={AXIS_Y + 5}
            stroke={LINE}
          />
          <text
            x={scaleX(value)}
            y={AXIS_Y + 18}
            fontSize="10"
            fill={INK2}
            textAnchor="middle"
          >
            {value === 0 ? t("same size") : `${Math.abs(value)}%`}
          </text>
        </g>
      ))}

      {[-1, 1].map((sign) => (
        <g key={`threshold-${sign}`}>
          <line
            x1={scaleX(sign * significantAt)}
            y1={AXIS_Y - 11}
            x2={scaleX(sign * significantAt)}
            y2={AXIS_Y + 9}
            stroke={WARM}
            strokeWidth={1.5}
            strokeDasharray="3 2"
          />
          <text
            x={(scaleX(sign * significantAt) + scaleX(sign * domain)) / 2}
            y={AXIS_Y + 32}
            fontSize="9"
            fill={WARM}
            textAnchor="middle"
          >
            {t("{{percent}}% and beyond", { percent: significantAt })}
          </text>
        </g>
      ))}

      <text x={MARGIN - 38} y={12} fontSize="10" fill={INK2}>
        {t("Left eye larger")}
      </text>
      <text
        x={WIDTH - MARGIN + 38}
        y={12}
        fontSize="10"
        fill={INK2}
        textAnchor="end"
      >
        {t("Right eye larger")}
      </text>

      {labels.map((label) => {
        const colour = Math.abs(label.percent) >= significantAt ? WARM : INK;
        return (
          <g key={label.text}>
            <line
              x1={label.x}
              y1={label.y + 4}
              x2={label.x}
              y2={AXIS_Y}
              stroke={colour}
            />
            <circle cx={label.x} cy={AXIS_Y} r={4.5} fill={colour} />
            <text
              x={label.textX}
              y={label.y}
              fontSize="11"
              fontWeight={600}
              fill={colour}
              textAnchor={label.anchor}
            >
              {label.text}
            </text>
          </g>
        );
      })}
    </Box>
  );
};

export default SizeScale;
